import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiX, FiAlertTriangle, FiAlertCircle, FiAlertOctagon, FiClock, FiCreditCard, FiZap } from 'react-icons/fi';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useStateContext } from '../../contexts/ContextProvider';

const SESSION_KEY = 'sub_reminder_dismissed';

const STATUS_CONFIG = {
  grace: {
    icon: <FiAlertTriangle className="w-7 h-7" />,
    iconBg: 'bg-yellow-100 text-yellow-600',
    badge: 'bg-yellow-100 text-yellow-800',
    label: 'Grace Period',
    heading: 'Your subscription is overdue',
    body: 'You are currently in a grace period. Some features may be restricted soon. Please renew your subscription to avoid interruption.',
  },
  restricted: {
    icon: <FiAlertCircle className="w-7 h-7" />,
    iconBg: 'bg-orange-100 text-orange-600',
    badge: 'bg-orange-100 text-orange-800',
    label: 'Restricted',
    heading: 'Your account is restricted',
    body: 'Write access has been disabled due to an overdue subscription. Renew now to restore full access for your school.',
  },
  suspended: {
    icon: <FiAlertOctagon className="w-7 h-7" />,
    iconBg: 'bg-red-100 text-red-600',
    badge: 'bg-red-100 text-red-800',
    label: 'Suspended',
    heading: 'Your account has been suspended',
    body: 'Access to your school dashboard has been suspended. Please make a payment immediately to restore access.',
  },
  pending: {
    icon: <FiClock className="w-7 h-7" />,
    iconBg: 'bg-blue-100 text-blue-600',
    badge: 'bg-blue-100 text-blue-800',
    label: 'Payment Pending',
    heading: 'Subscription payment pending',
    body: 'Your subscription payment is being processed. If you haven\'t completed payment, please do so now to avoid any service interruption.',
  },
  none: {
    icon: <FiAlertTriangle className="w-7 h-7" />,
    iconBg: 'bg-gray-100 text-gray-600',
    badge: 'bg-gray-100 text-gray-700',
    label: 'No Subscription',
    heading: 'No active subscription found',
    body: 'Your school does not have an active subscription. Subscribe now to unlock all features and manage your school seamlessly.',
  },
};

const SubscriptionReminderModal = () => {
  const { subscriptionStatus, hasNoSubscription, loading, endpointsUnavailable, canManageBilling } = useSubscription();
  const { user } = useStateContext();
  const history = useHistory();
  const [open, setOpen] = useState(false);

  const statusKey = hasNoSubscription ? 'none' : subscriptionStatus;
  const config = STATUS_CONFIG[statusKey];
  const shouldShow = !loading && !endpointsUnavailable && canManageBilling && Boolean(config) && statusKey !== 'active';

  // Clear the dismissed flag whenever a new user logs in so the modal shows again
  useEffect(() => {
    if (user?._id) sessionStorage.removeItem(SESSION_KEY);
  }, [user?._id]);

  useEffect(() => {
    if (!shouldShow) return;
    const dismissed = sessionStorage.getItem(SESSION_KEY);
    if (!dismissed) setOpen(true);
  }, [shouldShow]);

  const handleClose = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  const handleGoToBilling = (plan = '') => {
    handleClose();
    history.push(plan ? `/billing?plan=${plan}` : '/billing');
  };

  if (!open || !config) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Top accent bar */}
        <div className={`h-1.5 w-full ${
          statusKey === 'suspended' ? 'bg-red-500'
          : statusKey === 'restricted' ? 'bg-orange-500'
          : statusKey === 'grace' ? 'bg-yellow-400'
          : 'bg-blue-500'
        }`} />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${config.iconBg}`}>
              {config.icon}
            </div>
            <div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
                {config.label}
              </span>
              <h2 className="text-base font-bold text-gray-900 mt-1">{config.heading}</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors mt-0.5"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-5">
          <p className="text-sm text-gray-600 leading-relaxed mb-5">{config.body}</p>

          {/* Payment options */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => handleGoToBilling('full')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-indigo-200 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-indigo-600 group-hover:bg-indigo-700 flex items-center justify-center transition-colors">
                <FiCreditCard className="w-4 h-4 text-white" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-indigo-800">Instant Payment</p>
                <p className="text-xs text-indigo-500 mt-0.5">Pay full amount now</p>
              </div>
            </button>

            <button
              onClick={() => handleGoToBilling('installment')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-purple-600 group-hover:bg-purple-700 flex items-center justify-center transition-colors">
                <FiZap className="w-4 h-4 text-white" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-purple-800">Flexible Payment</p>
                <p className="text-xs text-purple-500 mt-0.5">Split into installments</p>
              </div>
            </button>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleGoToBilling}
              className="flex-1 py-2.5 text-sm font-semibold text-white bg-[#3F3DA5] hover:bg-indigo-700 rounded-xl transition-colors"
            >
              Go to Billing
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionReminderModal;
